import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle, openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { recordGraphicsModified } from "../entities";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface SetPhotoMarkerAttributeParams {
  blockHandle: string;
  attrHandle: string;
  oldValue: string;
  newValue: string;
}

export class SetPhotoMarkerAttributeCommand implements ICommand {
  private oplogEvents: OplogEmit[] = [];

  constructor(
    private readonly viewer: DRDrawingViewer,
    private readonly params: SetPhotoMarkerAttributeParams,
  ) {}

  /**
   * Oplog émis sur `blockHandle` (le marqueur, tel qu'affiché dans le fil) et non
   * `attrHandle` (l'attribut a son propre DR_META mais n'apparaît jamais comme ligne
   * séparée dans le scan live — cf. `parsing/activity.ts`).
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      {
        handle: this.params.blockHandle,
        changeKind: "photoNumber",
        oldValue: this.params.oldValue,
        newValue: this.params.newValue,
      },
    ]);
  }

  undo(): void {
    this.apply(this.params.oldValue);
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    this.apply(this.params.newValue);
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {}

  private apply(value: string): void {
    const { module, database } = this.viewer;
    const attrId = findEntityByHandle(module, database, this.params.attrHandle);
    if (!attrId || attrId.isNull()) return;
    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);
    if (attr) {
      const odValue = new module.OdString(value);
      attr.setTextString(odValue);
      recordGraphicsModified(module, attrId);
      deleteObj(odValue);
      deleteObj(attr);
    }
    deleteObj(attrId);
  }
}
